import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DBPRO Brand Colors
const BRAND = {
  navyDark: '#0A1A2F',
  navyMedium: '#0F2847',
  inputBg: '#1A3A5C',
  red: '#E53935',
  redHover: '#C62828',
  white: '#FFFFFF',
  lightGray: '#B0BEC5',
  blue: '#2196F3',
  error: '#EF5350',
  success: '#4CAF50'
};

// Logo URL
const DBPRO_LOGO_URL = 'https://customer-assets.emergentagent.com/job_gps-tracker-dash-1/artifacts/awchbld5_Final%20brand%20logo.png';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Redirect if no token
  useEffect(() => {
    if (!token) {
      navigate('/', { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Failed to reset password');
        setIsLoading(false);
        return;
      }
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
      
    } catch (err) {
      console.error('Reset password error:', err);
      setError('An error occurred. Please try again.');
    }
    
    setIsLoading(false);
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: BRAND.navyDark }}
        data-testid="reset-success-screen"
      >
        <div className="text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: BRAND.success }} />
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: BRAND.white }}
          >
            Password Reset Successful!
          </h1>
          <p style={{ color: BRAND.lightGray }} className="mb-4">
            Your password has been updated. Redirecting to login...
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-sm font-medium"
            style={{ color: BRAND.blue }}
            data-testid="go-to-login-link"
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: BRAND.navyDark }}
      data-testid="reset-password-screen"
    >
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo */}
        <img
          src={DBPRO_LOGO_URL}
          alt="DBPRO Logo"
          className="h-16 md:h-20 w-auto object-contain mb-8"
          data-testid="reset-logo"
        />

        {/* Title */}
        <h1 
          className="text-2xl md:text-3xl font-bold mb-2 text-center"
          style={{ color: BRAND.white }}
          data-testid="reset-title"
        >
          Reset Your Password
        </h1>
        <p 
          className="text-base mb-8 text-center"
          style={{ color: BRAND.lightGray }}
        >
          Enter your new password below
        </p>

        {/* Error Message */}
        {error && (
          <div 
            className="w-full max-w-sm mb-4 p-3 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: `${BRAND.error}20`, color: BRAND.error }}
            data-testid="reset-error"
          >
            <XCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Reset Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {/* New Password Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: BRAND.white }}
            >
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-4 py-3 pr-12 rounded-lg text-base focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: BRAND.inputBg,
                  color: BRAND.white
                }}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: BRAND.lightGray }}
                data-testid="new-password-toggle"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="mt-1 text-xs" style={{ color: BRAND.lightGray }}>
              Must be at least 8 characters
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: BRAND.white }}
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 pr-12 rounded-lg text-base focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: BRAND.inputBg,
                  color: BRAND.white
                }}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: BRAND.lightGray }}
                data-testid="confirm-password-toggle"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-lg text-lg font-bold transition-all disabled:opacity-70"
            style={{ 
              backgroundColor: BRAND.red,
              color: BRAND.white
            }}
            data-testid="reset-submit-button"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        {/* Back to Login */}
        <button
          onClick={() => navigate('/', { replace: true })}
          className="mt-6 flex items-center gap-2 text-sm"
          style={{ color: BRAND.lightGray }}
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>
      </div>

      {/* Footer */}
      <div className="pb-6 px-6">
        <p 
          className="text-center text-base font-bold tracking-wider"
          style={{ color: BRAND.white }}
        >
          TRAIN. TRACK. WIN.
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
